import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';
import { mapTripDetail, type RawTripDetail } from '@/lib/api/adapters/trips';
import type { TripDetail } from '@/hooks/trips/types';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

async function fetchTrip(id: string): Promise<TripDetail> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${id}`);
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to fetch trip ${id}: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('Trip details response is malformed.');
  }

  return mapTripDetail(payload as RawTripDetail);
}

export function useTrip(id: string) {
  return useQuery({
    queryKey: queryKeys.trip(id),
    queryFn: () => fetchTrip(id),
    enabled: !!id && id !== '' && id !== 'skip-fetch',
  });
}
